import { DocumentApi } from 'Api/Document/DocumentApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { UserDefinedReference } from 'Components/UserDefinedReference/UserDefinedReference';
import { EXCEPTIONS_COMPENSATING_CONTROLS, REFERENCE } from 'Config/Tooltips';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { toTitleCase } from 'Helpers/StringUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { ExceptionHistoryResponse, ExceptionResponse, ExceptionStatus } from 'Models/Exceptions';
import styles from 'Styles/DetailsPage.module.css';

interface ExceptionDetailsCurrentSnapshotProps {
    documentApi: DocumentApi;
    exceptionResponse: ExceptionResponse;
    type: 'current';
}

interface ExceptionDetailsHistoricalSnapshotProps {
    documentApi: DocumentApi;
    exceptionHistoryResponse: ExceptionHistoryResponse;
    type: 'historical';
}

type ExceptionDetailsSnapshotProps = ExceptionDetailsCurrentSnapshotProps | ExceptionDetailsHistoricalSnapshotProps;

/**
 * Renders details for an exception (current or historical), minus the impacted controls or impacted third party.
 * Used in the "main" area of the Exception Details page, and in historical modals on the same page.
 */
export const ExceptionDetailsSnapshot = (props: ExceptionDetailsSnapshotProps) => {
    const cachedData = useCachedData();

    const exception = props.type === 'current' ? props.exceptionResponse : props.exceptionHistoryResponse.exception;
    const tags = props.type === 'current' ? cachedData.convertTagIdsToDisplayableTags(props.exceptionResponse.tags) : props.exceptionHistoryResponse.exception.tags;

    /** Returns a displayable value for a given string, or '-' if the value is empty or undefined. */
    const getDisplayValue = (value?: string): string => {
        if (!value) {
            return '-';
        }
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : '-';
    };

    /** Returns a displayable user name for a given subject, or '-' if the subject is undefined or the user cannot be found. */
    const getUserDisplayName = (subject?: string): string => {
        if (!subject) {
            return '-';
        }
        return getUserNameFromSubject(subject, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? '-';
    };

    /** Returns a title-case display value for a given string, or '-' if the value is empty or undefined. */
    const getTitleCaseDisplayValue = (value?: string, separator?: string): string => {
        const titleCaseValue = toTitleCase(value, separator);
        return titleCaseValue ? titleCaseValue : '-';
    };

    return (
        <div className={styles.detailsContainer}>
            <div className={styles.detailsGridSection}>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Status" />
                        <Text variant="Text2">{getTitleCaseDisplayValue(exception.status)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Owner" />
                        <Text>{getUserDisplayName(exception.owner_subject)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Created" />
                        <Text>{`${iso8601ToUsDateShort(exception.created_timestamp)} by ${getUserNameFromSubject(exception.created_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}`}</Text>
                    </div>
                </div>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Expiration Date" />
                        <Text variant="Text2">{exception.expiration_date ? iso8601ToUsDateShort(exception.expiration_date) : '-'}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Reviewer" />
                        <Text>{getUserDisplayName(exception.reviewer_subject)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Likelihood" />
                        <Text>{getTitleCaseDisplayValue(exception.likelihood)}</Text>
                    </div>
                </div>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Risk Score" />
                        <Text variant="Text2">{getTitleCaseDisplayValue(exception.risk_score, '/')}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Delegates" />
                        <Text>{exception.delegates.length === 0 ? '-' : exception.delegates.map((delegate) => getUserDisplayName(delegate)).join(', ')}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Impact" />
                        <Text>{getTitleCaseDisplayValue(exception.impact, '/')}</Text>
                    </div>
                </div>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Reference" tooltipText={REFERENCE} />
                <UserDefinedReference reference={exception.reference} />
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Description" />
                <Text preserveLineBreaks>{getDisplayValue(exception.description)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Risk Description" />
                <Text preserveLineBreaks>{getDisplayValue(exception.risk_assessment)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Remediation Plan" />
                <Text preserveLineBreaks>{getDisplayValue(exception.remediation_plan)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Compensating Controls" tooltipText={EXCEPTIONS_COMPENSATING_CONTROLS} />
                <Text preserveLineBreaks>{getDisplayValue(exception.compensating_controls)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Files" />
                {exception.files.length === 0 ? <Text>-</Text> : [...exception.files].sort((a, b) => (a.filename > b.filename ? 1 : -1)).map((file) => <UploadedFileAndState key={file.file_id} file={file} documentApi={props.documentApi} />)}
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Closure Statement" />
                <Text preserveLineBreaks>{exception.status === ExceptionStatus.DRAFT_CLOSE || exception.status === ExceptionStatus.CLOSED ? getDisplayValue(exception.closure_statement) : '-'}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Tags" />
                {exception.tags.length > 0 ? <TagListing tags={tags} showAll /> : <Text noStyles>-</Text>}
            </div>
        </div>
    );
};
